import { Controller } from "@hotwired/stimulus"

const FIELDS = "input:not([type='hidden']):not([readonly]):not([disabled]), select:not([disabled]), textarea:not([readonly]):not([disabled])"
const CONTROLS = "button:not([disabled]), a[href]"

// The lines of the send checklist (_send_checklist): each opens the place that corrects its reason, scrolled into
// view and focused. A token names that place, "type:id:panel:step": "marking:12:wizard:textiles",
// "marking:12:pricing", "textile:5:sizes", "estimate:validity_date". It reuses what the editor already does: the
// card toggles of erp--estimate-editor, the "Modifier" button of block 1, erp--marking-wizard#openOn.
// The estimate page links to the editor with `?focus=<token>`: the editor opens that place once loaded and drops
// the parameter. erp--autosave replaces the checklist after each save, which connects this controller again.
// Connects to data-controller="erp--send-checklist"
export default class extends Controller {
  connect() {
    const url = new URL(window.location.href)
    const token = url.searchParams.get("focus")
    if (!token) return

    url.searchParams.delete("focus")
    window.history.replaceState(window.history.state, "", url)
    // The cards and the wizards connect their own controllers in the same pass
    this._frame = requestAnimationFrame(() => this.#reveal(token))
  }

  disconnect() {
    cancelAnimationFrame(this._frame)
  }

  go(event) {
    event.preventDefault()
    this.#reveal(event.params.focus)
  }

  #reveal(token) {
    const [ type, ...rest ] = String(token).split(":")
    if (type === "textile") this.#revealTextile(...rest)
    else if (type === "marking") this.#revealMarking(...rest)
    else this.#revealEstimate(rest[0])
  }

  // ── Estimate ─────────────────────────────────────────────────

  #revealEstimate(panel) {
    if (panel === "validity_date") return this.#revealValidity()
    if (panel === "global_discount") return this.#revealGlobalDiscount()

    const ids = { textiles: "estimate-block-textiles", markings: "estimate-block-markings", approvals: "estimate-approvals" }
    const block = document.getElementById(ids[panel])
    this.#show(block, this.#firstControl(block))
  }

  // Block 1 folds into a summary once saved: its "Modifier" button unfolds it
  #revealValidity() {
    const field = document.querySelector("[data-erp--estimate-general-target='validity']")
    if (!field) return

    if (field.closest("[hidden]")) document.querySelector("[data-erp--estimate-general-target='edit']")?.click()
    this.#show(field, field)
  }

  // The select is shown as pills (erp--select-chips): the chosen one takes the focus
  #revealGlobalDiscount() {
    const container = document.getElementById("estimate_global_discount_choice")?.parentElement
    const pills = container?.querySelector("[data-erp--select-chips-target='choices']")
    this.#show(container, pills?.querySelector("[aria-pressed='true']") || pills?.querySelector("button"))
  }

  // ── Textile ──────────────────────────────────────────────────

  #revealTextile(id, panel) {
    const card = document.querySelector(`[data-erp--estimate-editor-target='item'][data-item-id='${CSS.escape(id)}']`)
    if (!card) return this.#revealEstimate("textiles")

    if (card.querySelector("[data-erp--estimate-editor-target='itemForm']")?.classList.contains("hidden")) {
      card.querySelector("[data-item-toggle]")?.click()
    }
    const part = panel === "pricing"
      ? card.querySelector("[data-controller~='erp--estimate-item-pricing']")
      : card.querySelector("[data-erp--estimate-editor-target='sizeList']")
    this.#show(part || card, this.#firstControl(part || card))
  }

  // ── Marking ──────────────────────────────────────────────────

  #revealMarking(id, panel, step) {
    const card = this.#markingCard(id)
    if (!card) return this.#revealEstimate("markings")
    if (panel === "pricing") return this.#revealMarkingPricing(card)

    card.scrollIntoView({ block: "center" })
    // openOn opens the wizard on the step holding the element it is given
    const pane = card.querySelector(`[data-erp--marking-wizard-target='pane'][data-step='${CSS.escape(step || "")}']`)
    this.application.getControllerForElementAndIdentifier(card, "erp--marking-wizard")?.openOn(pane || card)
  }

  #revealMarkingPricing(card) {
    const panel = card.querySelector("[data-erp--estimate-editor-target='markingForm']")
    if (panel?.classList.contains("hidden")) card.querySelector("[data-marking-toggle]")?.click()
    this.#show(panel || card, this.#firstControl(panel || card))
  }

  // The first `[id]` field of a marking card is its own (erp--autosave fills it for a new marking)
  #markingCard(id) {
    return [ ...document.querySelectorAll("[data-erp--estimate-editor-target='marking']") ]
      .find(card => card.querySelector("input[name$='[id]']")?.value === String(id))
  }

  // ── Scroll and focus ─────────────────────────────────────────

  #show(container, control) {
    if (!container) return

    container.scrollIntoView({ block: container.offsetHeight < window.innerHeight ? "center" : "start", behavior: "smooth" })
    const target = control || container
    if (!control) container.setAttribute("tabindex", "-1")
    target.focus({ preventScroll: true })
  }

  #firstControl(container) {
    if (!container) return null

    const visible = element => element.checkVisibility()
    return [ ...container.querySelectorAll(FIELDS) ].find(visible) || [ ...container.querySelectorAll(CONTROLS) ].find(visible) || null
  }
}
